import {
  InitiativeSelectorDialogState,
  initiativeSelectorDialogState,
  InitiativesSelectorDialogOpenState
} from './store/State'
import IProfileInitiativeStatus from 'types/sign-in/IProfileInitiativeStatus'
import initiativesSelectorDialogReducer from './store/Reducer'
import {
  setIsDialogOpen,
  setInitiativesOptions,
  reset,
  toggleIsLoading,
  InitiativeSelectorDialogActions
} from './store/Actions'
import ServiceProviderBase from 'lib/ServiceProviderBase'
import { Http } from 'lib'
import apiUrls from 'config/constants/api-urls-addresses'
import { IUserResponses } from 'types'

class InitiativesSelectorDialogService extends ServiceProviderBase<
  InitiativeSelectorDialogState,
  InitiativeSelectorDialogActions
> {
  constructor() {
    super(initiativeSelectorDialogState)
  }

  reduce(state: InitiativeSelectorDialogState, action: InitiativeSelectorDialogActions): InitiativeSelectorDialogState {
    return initiativesSelectorDialogReducer(state, action)
  }

  setIsDialogOpen(isOpenState: InitiativesSelectorDialogOpenState) {
    this.dispatch(setIsDialogOpen(isOpenState))
  }

  setDialogStateAndInitiativeOptions(
    isOpenState: InitiativesSelectorDialogOpenState,
    options: IProfileInitiativeStatus[]
  ) {
    this.dispatch(setInitiativesOptions(isOpenState, options))
  }

  executeStateMachine(userResponses: IUserResponses, pendingInitiatives: IProfileInitiativeStatus[]) {
    const self = this

    const possibleStates = [
      {
        id: '1',
        isMatch(
          componentState: InitiativeSelectorDialogState,
          responses: IUserResponses,
          initiatives: IProfileInitiativeStatus[]
        ) {
          const totals = responses?.validation?.total
          const shouldOpen = totals?.isComplete && initiatives.length > 0

          return shouldOpen && ['UNDEFINED', 'UNDEFINED_WITH_INITIATIVES'].includes(componentState.isDialogOpenState)
        },
        onMatch(initiatives: IProfileInitiativeStatus[]) {
          self.setDialogStateAndInitiativeOptions('OPEN', initiatives)
        }
      },
      {
        id: '2',
        isMatch(
          componentState: InitiativeSelectorDialogState,
          _responses: IUserResponses,
          initiatives: IProfileInitiativeStatus[]
        ) {
          return componentState.isDialogOpenState === 'UNDEFINED' && initiatives.length
        },
        onMatch(initiatives: IProfileInitiativeStatus[]) {
          self.setDialogStateAndInitiativeOptions('UNDEFINED_WITH_INITIATIVES', initiatives)
        }
      },
      {
        id: '3',
        isMatch(
          componentState: InitiativeSelectorDialogState,
          _responses: IUserResponses,
          initiatives: IProfileInitiativeStatus[]
        ) {
          return (
            componentState.isDialogOpenState === 'OPEN' &&
            componentState.initiativesOptions.length !== initiatives.length &&
            initiatives.length > 0
          )
        },
        onMatch(initiatives: IProfileInitiativeStatus[]) {
          self.setDialogStateAndInitiativeOptions('OPEN', initiatives)
        }
      },
      {
        id: '4',
        isMatch(
          componentState: InitiativeSelectorDialogState,
          _responses: IUserResponses,
          initiatives: IProfileInitiativeStatus[]
        ) {
          return initiatives.length === 0 && componentState.isDialogOpenState === 'OPEN'
        },
        onMatch() {
          self.setDialogStateAndInitiativeOptions('UNDEFINED', [])
        }
      }
    ]

    const stateHandler = possibleStates.find(it => it.isMatch(self.state, userResponses, pendingInitiatives))

    if (stateHandler) {
      stateHandler.onMatch(pendingInitiatives)
    } else {
      this.setDialogStateAndInitiativeOptions(this.state.isDialogOpenState, pendingInitiatives)
    }
  }

  async loadInitiativeOptions(userResponses: IUserResponses): Promise<string | null> {
    if (!userResponses || this.state.isLoading) {
      return null
    }

    this.dispatch(toggleIsLoading())

    const [data, , error] = await Http.get<{
      initiatives: IProfileInitiativeStatus[]
    }>(apiUrls.me.listInitiatives())

    if (error) {
      this.dispatch(toggleIsLoading())
      return error
    }

    const pendingInitiatives = data.initiatives.filter(it => !it.completion_date)

    this.executeStateMachine(userResponses, pendingInitiatives)

    this.dispatch(toggleIsLoading())

    return null
  }

  reset() {
    this.dispatch(reset())
  }
}

let instance: InitiativesSelectorDialogService = null

export default (() => {
  if (!instance) {
    instance = new InitiativesSelectorDialogService()
  }
  return instance
})()
